import { XMarkIcon } from "@heroicons/react/24/outline";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Outlet, useParams } from "react-router-dom";

import { useEnrichedHouseholds } from "@/api/hooks/useEnrichedHouseholds";
import { useUser, useUserStatus } from "@/api/hooks/useUser";
import { fetchHouseholdData, updateHouseholdFeatureFlags } from "@/api/rest/services/householdService";
import ClosableBanner from "@/components/common/ClosableBanner";
import LoadFailed from "@/components/common/LoadFailed";
import Markdown from "@/components/common/Markdown";
import TallyFormPopup from "@/components/common/TallyFormPopup";
import Navbar from "@/components/Navbar";
import { debug } from "@/utils/debug/logger";
import { useDebugLifecycle } from "@/utils/debug/useDebugLifecycle";

const TALLY_FORM_FLAG = "TALLY_FORM_URL";

export default function LoggedInPage() {
  const { householdId, buildingId } = useParams<{ householdId: string; buildingId: string }>();
  const { t, i18n } = useTranslation();
  const user = useUser();
  const { status: userStatus, retry: retryUser } = useUserStatus();
  const enrichedHouseholds = useEnrichedHouseholds(user);
  const households = useMemo(() => enrichedHouseholds.filter((h) => h.buildingId !== -1), [enrichedHouseholds]);
  const queryClient = useQueryClient();

  const currentHousehold = useQuery({
    queryKey: ["household", householdId, i18n.language],
    queryFn: () => fetchHouseholdData(Number(householdId), i18n.language),
    enabled: Boolean(householdId),
  });

  const tallyFeature = currentHousehold.data?.result?.featureFlags?.[TALLY_FORM_FLAG];
  const isFormCompleted = currentHousehold.data?.result?.featureFlags?.TALLY_FORM_COMPLETED;
  const hasTallyForm = Boolean(tallyFeature && tallyFeature.tallyFormUrl && householdId && !isFormCompleted);

  const [isTallyPopupVisible, setIsTallyPopupVisible] = useState(false);
  const [isBannerVisible, setIsBannerVisible] = useState(false);
  const [isDemoFeedbackVisible, setIsDemoFeedbackVisible] = useState(true);
  const showCallLinks = currentHousehold.data?.result?.featureFlags?.showCallLinks;

  useEffect(() => {
    if (hasTallyForm && !isBannerVisible) {
      console.log("Setting Tally popup visible");
      setIsTallyPopupVisible(true);
    }
  }, [hasTallyForm, isBannerVisible]);

  const handleTallyFormComplete = async () => {
    if (!householdId) {
      return;
    }
    await updateHouseholdFeatureFlags(householdId, {
      TALLY_FORM_COMPLETED: true,
    });
    // Refetch the household data to get the new state without the flag
    await queryClient.invalidateQueries({
      queryKey: ["household", householdId],
    });
    setIsTallyPopupVisible(false);
  };

  const handleTallyFormClose = () => {
    setIsTallyPopupVisible(false);
    setIsBannerVisible(true);
  };

  const handleBannerAction = () => {
    setIsTallyPopupVisible(true);
  };

  debug.log("LoggedInPage", { households, currentHousehold, user });

  useDebugLifecycle("LoggedInPage");

  return (
    <>
      {showCallLinks && isDemoFeedbackVisible && (
        <div className="fixed top-0 left-0 right-0 z-50">
          <div className="max-w-4xl mx-auto">
            <div className="m-4 rounded-lg p-4 flex justify-between items-center shadow-lg bg-blue-100">
              <span className="text-blue-800">
                Please give us feedback!{" "}
                <a
                  href="https://app.userjourneys.ai/i/phHK2ssJ"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-800 underline font-semibold hover:text-blue-900"
                >
                  Click here to share your thoughts
                </a>
              </span>
              <button
                onClick={() => setIsDemoFeedbackVisible(false)}
                className="ml-4 p-1 hover:bg-black/10 rounded-full transition-colors"
                aria-label="Close banner"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}
      {isBannerVisible && tallyFeature?.bannerText && (
        <ClosableBanner
          message={
            <div className="flex items-center justify-between w-full">
              <div className="prose prose-sm max-w-none [&_a]:text-blue-800 [&_a]:underline [&_a]:font-semibold hover:[&_a]:text-blue-900">
                <Markdown
                  components={{
                    p: ({ children }) => <span>{children}</span>,
                    a: ({ href, children }) => (
                      <a href={href} target="_blank" rel="noopener noreferrer">
                        {children}
                      </a>
                    ),
                  }}
                >
                  {tallyFeature.bannerText}
                </Markdown>
              </div>
              <button
                onClick={handleBannerAction}
                className="ml-4 px-4 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors whitespace-nowrap"
              >
                {t("loggedInPage.tallyFormBanner.button")}
              </button>
            </div>
          }
          storageKey={`tally-form-banner-dismissed-${householdId}`}
          dismissDurationMs={60 * 60 * 1000}
        />
      )}
      {isTallyPopupVisible && tallyFeature?.tallyFormUrl && (
        <TallyFormPopup
          tallyFormUrl={tallyFeature.tallyFormUrl}
          onComplete={handleTallyFormComplete}
          onClose={handleTallyFormClose}
        />
      )}
      <div className="flex h-screen w-screen flex-col justify-stretch overflow-y-auto md:overflow-hidden md:flex-row">
        <Navbar households={households} currentHousehold={currentHousehold} user={user} buildingId={buildingId} />
        {userStatus === "error" || currentHousehold.isError ? (
          <LoadFailed
            onRetry={() => {
              if (userStatus === "error") {
                void retryUser();
              }
              if (currentHousehold.isError) {
                void currentHousehold.refetch();
              }
            }}
          />
        ) : (
          <Outlet context={{ user, currentHousehold }} />
        )}
      </div>
    </>
  );
}
